/* eslint-disable sort-keys-fix/sort-keys-fix */
import React from 'react';
import * as R from 'ramda';
import { bool, shape } from 'prop-types';

// Utils
import { getDay } from 'date-fns';
import { convertMilitaryTimeString } from 'feature/loading-management/utils/timeHelpers';

// Components
import { Avatar, Box, Typography, useMediaQuery } from '@mui/material';
import WatchLater from '@mui/icons-material/WatchLaterOutlined';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded';
import TimeSectionSkeletons from './TimeSectionSkeletons';

const TimeSection = props => {
  const { activeLocation, locationDetails, locationLoadTimes, pendingState } = props;
  const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'), { noSsr: true });

  const dayOfWeek = getDay(new Date());

  const nextBestLoadTime = waitTimesArray =>
    R.cond([
      // All wait times are below the 30 minute threshold
      [R.all(R.propSatisfies(time => R.lt(time, 30), 'minutes')), R.always('Now')],
      // Any wait time is below the 30 minute threshold
      [
        R.any(R.propSatisfies(time => R.lt(time, 30), 'minutes')),
        R.compose(
          convertMilitaryTimeString,
          R.prop('key'),
          R.find(R.where({ minutes: R.lt(R.__, 30) }))
        ),
      ],
      // No wait time is below 30 minute threshold
      [
        R.T,
        () =>
          convertMilitaryTimeString(
            R.prop(
              'key',
              waitTimesArray.reduce((prev, current) =>
                prev.minutes <= current.minutes ? prev : current
              )
            )
          ),
      ],
    ])(waitTimesArray);

  const renderTimeSection = () => (
    <>
      <Box
        // Allows grid to flow when switching to mobile, contains styling for border and margins to match height
        sx={{
          borderRight: theme => ({ xs: `1px solid ${theme.palette.graphic.light}`, md: 'none' }),
          display: 'grid',
          gridTemplateRow: '1fr 1fr',
          height: { xs: 'initial', md: 32 },
          gridRow: { xs: '1 / 3', md: '' },
          gridTemplate: { md: '1fr 1fr / 40px 1fr' },
          ml: { xs: 0, md: 'initial' },
          mr: { xs: 5, md: 'initial' },
          my: { xs: 0.5, md: 'initial' },
        }}
      >
        <Avatar
          sx={{
            alignSelf: 'center',
            backgroundColor: 'primary.lighter',
            color: 'primary.dark',
            display: { xs: 'none', md: 'flex' },
            gridRow: '1 / 3',
            height: 32,
            justifySelf: 'flex-end',
            marginBottom: 0.5,
            width: 32,
          }}
        >
          <WatchLater />
        </Avatar>
        <Typography
          sx={{
            alignSelf: 'flex-end',
            color: 'primary.dark',
            lineHeight: '20px',
            ml: { xs: 0, md: 1 },
          }}
          variant={mdUp ? 'h6' : 'h5'}
        >
          {/* TODO: Fix for loading data */}
          {/* Short circuiting to prevent crash from failed endpoint call */}
          {!R.isEmpty(activeLocation) && !R.isNil(activeLocation?.waitTimes[0])
            ? `${activeLocation?.waitTimes[0].minutes} mins`
            : 'N/A'}
        </Typography>
        <Typography sx={{ lineHeight: '16px', marginLeft: { xs: 0, md: 1 } }} variant="caption">
          Current Wait Times
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRow: '1fr 1fr',
          height: { xs: 'initial', md: 32 },
          gridTemplate: { md: '1fr 1fr / 40px 1fr' },
          pb: { xs: 1, md: 'initial' },
        }}
      >
        {mdUp && (
          <Avatar
            sx={{
              alignSelf: 'center',
              backgroundColor: 'blue.light',
              color: 'primary.dark',
              marginLeft: 1,
              gridRow: '1 / 3',
              height: 32,
              justifySelf: 'flex-end',
              marginBottom: 0.5,
              width: 32,
            }}
          >
            <HistoryToggleOffRoundedIcon />
          </Avatar>
        )}
        <Typography
          sx={{
            alignSelf: 'flex-end',
            color: 'primary.dark',
            lineHeight: '20px',
            marginLeft: { xs: 0, md: 1 },
          }}
          variant={mdUp ? 'h6' : 'subtitle2'}
        >
          {/* Short circuiting to prevent crash from failed endpoint call */}
          {!R.isEmpty(locationLoadTimes) && locationLoadTimes?.lastFourWeeksDailyAverages[dayOfWeek]
            ? `${locationLoadTimes?.lastFourWeeksDailyAverages[dayOfWeek]} mins`
            : !R.isEmpty(locationLoadTimes) && locationLoadTimes?.averageDailyLoadMinutes
            ? `${locationLoadTimes?.averageDailyLoadMinutes} mins`
            : 'N/A'}
        </Typography>
        <Typography sx={{ lineHeight: '16px', ml: { xs: 0, md: 1 } }} variant="caption">
          Average Load Time
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRow: '1fr 1fr',
          height: { xs: 'initial', md: 32 },
          gridTemplate: { md: '1fr 1fr / 40px 1fr' },
        }}
      >
        {mdUp && (
          <Avatar
            sx={{
              alignSelf: 'center',
              backgroundColor: 'blue.light',
              color: 'success.dark',
              gridRow: '1 / 3',
              height: 32,
              justifySelf: 'flex-end',
              mb: 0.5,
              ml: 1,
              width: 32,
            }}
          >
            <MoreTimeIcon />
          </Avatar>
        )}
        <Typography
          sx={{
            alignSelf: 'flex-end',
            color: 'primary.dark',
            lineHeight: '20px',
            ml: { xs: 0, md: 1 },
          }}
          variant={mdUp ? 'h6' : 'subtitle2'}
        >
          {/* Short circuiting to prevent crash from failed endpoint call */}
          {!R.isEmpty(locationDetails) && locationDetails?.hourlyWaitTimes
            ? nextBestLoadTime(locationDetails?.hourlyWaitTimes)
            : 'N/A'}
        </Typography>
        <Typography sx={{ lineHeight: '16px', ml: { xs: 0, md: 1 } }} variant="caption">
          {!R.isEmpty(locationDetails) &&
          R.none(R.propSatisfies(time => R.lt(time, 30), 'minutes'))(
            locationDetails?.hourlyWaitTimes
          )
            ? 'Next Shortest Load Time'
            : 'Next Best Load Time'}
        </Typography>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        backgroundColor: 'surface.white',
        borderBottom: theme => ({ xs: `1px solid ${theme.palette.graphic.light}`, lg: 'none' }),
        boxShadow: { xs: 'none', md: 5 },
        display: { xs: 'grid', md: 'flex' },
        flexDirection: { xs: 'revert', md: 'row' },
        gridTemplate: { xs: '1fr 1fr / 1fr 1fr', md: 'revert' },
        justifyContent: { xs: 'revert', md: 'space-around' },
        maxWidth: 1000,
        px: 3,
        py: { xs: 2, md: 4 },
        width: '100%',
      }}
    >
      {pendingState ? <TimeSectionSkeletons /> : renderTimeSection()}
    </Box>
  );
};

TimeSection.propTypes = {
  activeLocation: shape({}).isRequired,
  locationDetails: shape({}),
  locationLoadTimes: shape({}),
  pendingState: bool.isRequired,
};

TimeSection.defaultProps = {
  locationDetails: {},
  locationLoadTimes: {},
};

export default TimeSection;
